import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchPersonDayLogs} from '@/entities/log/day';
import {fetchMyDayLogs} from '@/entities/me/api';
import type {AccessLogRecord} from '@/entities/log/types';
import {ApiError} from '@/shared/api';
import {formatClock, formatSimilarity} from '@/shared/lib/format';
import {AppButton} from '@/shared/ui/button';
import {HelpNote} from '@/shared/ui/explain';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {Skeleton} from '@/shared/ui/skeleton';
import {fonts} from '@/shared/ui/fonts';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Все события одного человека за один день — сырой журнал под карточкой дня.
 *
 * ## Зачем он, если день уже расписан интервалами
 *
 * Табель показывает ВЫВОД: приход, уход, интервалы «внутри», вердикты «опоздал»,
 * «неполный день». Спор же всегда идёт про исходное. Здесь лежит оно: каждое
 * событие, а не первое и последнее; какая именно дверь и в какую сторону
 * (`gate_mode`) — из-за чего эпизод и мог сложиться не так, как человек помнит;
 * процент совпадения на каждом проходе и температура. Последних двух нет больше
 * нигде в кабинете: ни лента, ни архив журнала не показывают температуру, а
 * совпадение лента показывает только у прохода.
 *
 * ⚠️ **Отказов здесь, скорее всего, не будет — и это не пропажа.** Терминал пишет
 * отказ как проход «Неизвестного»: карточки у неопознанного человека нет, значит нет
 * и `record_id`, а сервер отбирает события ИМЕННО по нему. Плюс сама запись отказов
 * по умолчанию выключена на уровне компании (`log_denials`, changelog
 * 2026-08-13-1500; переключатель — в настройках компании). Непризнанная попытка
 * человека ищется поэтому не здесь, а в архиве журнала за тот день, фильтром
 * «запрещён». Ветка отказа в строке ниже всё равно нужна: если событие с
 * `granted: false` когда-нибудь приедет с `record_id`, оно обязано выглядеть отказом,
 * а не проходом.
 *
 * ## Один компонент на две персоны
 *
 * `recordId` задан — смотрит работодатель (`/control/logs?record_id=`), не задан —
 * сотрудник смотрит собственный день (`/me/logs`). Разные маршруты, разные модели
 * доступа, одна и та же лента на экране: у дня не может быть двух видов.
 *
 * ## Почему это ленивый запрос, а не часть отчёта
 *
 * Ни один маршрут журнала не принимает дату (см. `entities/log/day.ts`), поэтому день
 * собирается листанием ленты. Тридцать дней месяца, собранные заранее, — это тридцать
 * таких листаний; запрос живёт под РАСКРЫТОЙ карточкой дня и выполняется, только когда
 * этот день раскрыли.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(2)},
  head: {flexDirection: 'row', alignItems: 'baseline', gap: spacing(2)},
  headCount: {flex: 1, textAlign: 'right'},

  row: {flexDirection: 'row', alignItems: 'stretch', gap: spacing(2)},
  // Рельс вердикта: цвет строки читается раньше любого текста — как в ленте.
  rail: {width: 3, borderRadius: radii.pill},
  time: {width: 44, fontFamily: fonts.mono.medium, fontSize: 13, lineHeight: 18},
  body: {flex: 1, gap: 1},
  meta: {alignItems: 'flex-end', gap: 2, maxWidth: '42%'},
});

/**
 * Одно событие.
 *
 * `sameDoor` — предыдущая строка того же дня прошла через ту же дверь. Тогда адрес
 * терминала не повторяется: за день человек чаще всего ходит через одну проходную, и
 * четыре строки «Ташкент, Чиланзар · Цех» подряд — это не сведения, а фон, из-за
 * которого не видно единственного, что в этих строках действительно РАЗНОЕ: времени и
 * направления. Смена двери, наоборот, событие — и адрес возвращается ровно там.
 *
 * Отдел устройства не показывается вовсе: это журнал одного человека, и отдел его
 * проходной — та же константа, что и его собственный, только на строку длиннее.
 */
function EventRow({log, sameDoor}: {log: AccessLogRecord; sameDoor: boolean}) {
  const {t} = useTranslation();
  const p = useTheme();

  const railColor = log.granted ? p.signal : p.destructive;
  // Совпадение показывается у ОБОИХ исходов, а не только у прохода: у отказа это и
  // есть причина отказа, и рядом с ним нужен порог, который не был взят.
  const similarity =
    log.similarity_percent != null
      ? log.required_accuracy_percent != null && !log.granted
        ? `${formatSimilarity(log.similarity_percent)} / ${formatSimilarity(
            log.required_accuracy_percent,
          )}`
        : formatSimilarity(log.similarity_percent)
      : null;

  const under = [
    // `gate_mode` — свободная строка; известные три переводим, незнакомую печатаем как
    // есть, а не прячем: новый режим прошивки должен деградировать, а не исчезать.
    log.gate_mode
      ? t(`home.device.gate.${log.gate_mode}`, {defaultValue: log.gate_mode})
      : null,
    log.temperature_celsius != null
      ? t('dayLog.temperature', {value: log.temperature_celsius.toFixed(1)})
      : null,
    sameDoor ? null : log.location_label || null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <View style={styles.row} testID={`day-log-${log.event_id}`}>
      <View style={[styles.rail, {backgroundColor: railColor}]} />
      <AppText style={styles.time}>{formatClock(log.created_at_millis)}</AppText>
      <View style={styles.body}>
        {/* Та же дверь, что строкой выше, — название не повторяется, но строка не
            съезжает: её место занимает направление, ставшее здесь заголовком. */}
        {sameDoor ? null : (
          <AppText variant="subtitle" numberOfLines={1}>
            {log.device_name}
          </AppText>
        )}
        {under ? (
          <AppText
            variant={sameDoor ? 'body' : 'micro'}
            numberOfLines={1}
            color={sameDoor ? p.mutedForeground : undefined}>
            {under}
          </AppText>
        ) : null}
        {/* Сообщение терминала — только у отказа: у прохода оно пустое или служебное. */}
        {!log.granted && log.message ? (
          <AppText variant="micro" color={p.destructiveInk} numberOfLines={2}>
            {log.message}
          </AppText>
        ) : null}
      </View>
      <View style={styles.meta}>
        <StatusPill
          tone={log.granted ? 'granted' : 'denied'}
          size="sm"
          dot={false}
          label={t(log.granted ? 'myLogs.granted' : 'myLogs.denied')}
        />
        {similarity ? <AppText variant="micro">{similarity}</AppText> : null}
      </View>
    </View>
  );
}

export function DayLog({
  companyId,
  date,
  recordId,
  expectedCount,
  onOpenArchive,
}: {
  companyId: string;
  /** `YYYY-MM-DD`, ровно как его печатает движок табеля. */
  date: string;
  /**
   * `event_count` из табеля за этот же день — чтобы сверить два счёта одного и того же.
   *
   * Табель и журнал считают одни события, но приходят из разных мест: движок явки
   * посчитал их на сервере, а список ниже собран здесь, обходом ленты по границам
   * СУТОК ПО ВРЕМЕНИ ЭТОГО УСТРОЙСТВА. Совпадать они обязаны; расхождение — это либо
   * событие, не попавшее в расчёт явки, либо разъехавшиеся сутки, и в обоих случаях
   * это ровно тот факт, который всплывает в споре о зарплате. Пусть он всплывает
   * здесь и заранее.
   */
  expectedCount?: number | null;
  /** Задан — журнал сотрудника глазами работодателя; не задан — собственный. */
  recordId?: string;
  /**
   * Уйти в архив журнала за этот день. Только у работодателя: архив — экран компании,
   * и у персоны «сотрудник» его нет ни в правах, ни во вкладках. Отсюда и не кнопка
   * внутри компонента, а обязанность экрана — навигацию в кабинете делают страницы.
   */
  onOpenArchive?: () => void;
}) {
  const {t} = useTranslation();

  const query = useQuery({
    queryKey: ['day-logs', companyId, recordId ?? 'me', date],
    queryFn: () =>
      recordId
        ? fetchPersonDayLogs(companyId, recordId, date)
        : fetchMyDayLogs(companyId, date),
  });

  if (query.isPending) {
    return (
      <View style={styles.root}>
        <Skeleton height={18} radius={radii.sm} />
        <Skeleton height={18} radius={radii.sm} />
      </View>
    );
  }

  if (query.isError) {
    // Кабинет разделов не прячет и про отказ узнаёт из `403` (conventions.md §6) —
    // но `403` здесь не ошибка загрузки, а «журнал вам не показывают»: у роли может
    // быть отчёт по явке и не быть журнала проходов. Это состояние, а не сбой.
    const forbidden = query.error instanceof ApiError && query.error.status === 403;
    return forbidden ? (
      <HelpNote text={t('dayLog.forbidden')} />
    ) : (
      <View style={styles.root}>
        <AppText variant="caption">{t('dayLog.error')}</AppText>
        <AppButton
          tone="ghost"
          size="sm"
          title={t('actions.retry')}
          onPress={() => void query.refetch()}
        />
      </View>
    );
  }

  const {events, complete, scanned} = query.data;

  return (
    <View style={styles.root} testID={`day-log-${date}`}>
      <View style={styles.head}>
        <AppText variant="eyebrow">{t('dayLog.title')}</AppText>
        <AppText variant="micro" style={styles.headCount}>
          {t('dayLog.count', {count: events.length})}
        </AppText>
      </View>

      {events.length === 0 ? (
        <>
          <AppText variant="caption">
            {complete ? t('dayLog.empty') : t('dayLog.notReached')}
          </AppText>
          {/* Пустой день — единственное место, где стоит объяснить, чего здесь не
              бывает: у дня с проходами этот текст был бы шумом, а у пустого он и есть
              ответ на «а где отказы». */}
          {complete ? <HelpNote text={t('dayLog.hint')} /> : null}
        </>
      ) : (
        events.map((log, i) => (
          <EventRow
            key={log.event_id}
            log={log}
            sameDoor={i > 0 && events[i - 1].device_id === log.device_id}
          />
        ))
      )}

      {/* Честная граница просмотра: журнал не фильтруется по дате на сервере, и
          глубина листания конечна. Молча показать половину дня — хуже, чем сказать. */}
      {!complete ? <HelpNote text={t('dayLog.partial', {scanned})} /> : null}

      {/* Сверка с табелем — только когда день собран целиком: у недосмотренного дня
          расхождение объясняется предыдущей строкой и повторять его незачем. */}
      {complete &&
      expectedCount != null &&
      expectedCount !== events.length ? (
        <HelpNote
          testID="day-log-mismatch"
          text={t('dayLog.mismatch', {
            expected: expectedCount,
            actual: events.length,
          })}
        />
      ) : null}

      {/* Дальше этого дня — в архив: там тот же день, но с отказами неопознанных,
          которых в журнале одного человека не бывает, и с выгрузкой. */}
      {onOpenArchive ? (
        <AppButton
          title={t('dayLog.openArchive')}
          tone="ghost"
          size="sm"
          icon="logs"
          onPress={onOpenArchive}
          testID={`day-log-archive-${date}`}
        />
      ) : null}
    </View>
  );
}
